import { useState, useCallback, useEffect, useRef } from "react";
import useAgentStore from "./useAgentStore";
import usePublicChatStore from "./usePublicChatStore";
import { parsePublicErrorMessage } from "./agentSessionsPanelUtils";

/**
 * Encapsulates session selection, new-chat, and start-chat logic
 * for both private and public modes.
 */
export default function useSessionActions({
  agentId,
  agentName,
  isPublicMode,
  composerDisabled,
  publicShareToken,
  publicIdentityPayload,
  publicUserDisplayName,
  initialSessionId,
  onSessionSelect,
  showSidebar,
  chatContext,
  // from usePublicSessions
  pub,
}) {
  const [selectedSessionId, setSelectedSessionId] = useState(
    initialSessionId || null
  );
  const [isNewChat, setIsNewChat] = useState(false);
  const [startingChat, setStartingChat] = useState(false);

  const initialLoadDone = useRef(false);
  const prevStatusRef = useRef(null);

  const pubRef = useRef(pub);
  pubRef.current = pub;

  const resolvePrivateClientId = useCallback(() => {
    if (isPublicMode) return null;
    const currentDetail = useAgentStore.getState().sessionDetail;
    if (
      currentDetail &&
      currentDetail.chatSessionId === selectedSessionId &&
      Number.isFinite(Number(currentDetail.clientId)) &&
      Number(currentDetail.clientId) > 0
    ) {
      return Number(currentDetail.clientId);
    }
    const selectedRow = useAgentStore
      .getState()
      .sessions.find(
        (session) => session?.chatSessionId === selectedSessionId
      );
    const rowClientId = Number(selectedRow?.clientId);
    if (Number.isFinite(rowClientId) && rowClientId > 0) {
      return rowClientId;
    }
    return null;
  }, [isPublicMode, selectedSessionId]);

  const handleNewChat = useCallback(() => {
    setIsNewChat(true);
    setSelectedSessionId(null);
    if (!isPublicMode) {
      useAgentStore.getState().disconnectStream();
      useAgentStore.setState({ sessionDetail: null });
    } else {
      pubRef.current.resetPublicState();
    }
    if (onSessionSelect) onSessionSelect(null);
  }, [isPublicMode, onSessionSelect]);

  const handleSelectSession = useCallback(
    (chatSessionId) => {
      if (!isNewChat && chatSessionId === selectedSessionId) {
        setSelectedSessionId(null);
        if (!isPublicMode) {
          useAgentStore.getState().disconnectStream();
          useAgentStore.setState({ sessionDetail: null });
        } else {
          pubRef.current.resetPublicState();
        }
        if (onSessionSelect) onSessionSelect(null);
        return;
      }
      setIsNewChat(false);
      setSelectedSessionId(chatSessionId);
      if (!isPublicMode) {
        useAgentStore.getState().fetchSessionDetail(chatSessionId);
        if (onSessionSelect) onSessionSelect(chatSessionId);
        return;
      }
      const p = pubRef.current;
      const selectedRow = p.publicSessions.find(
        (session) => session?.chatSessionId === chatSessionId
      );
      const sessionToken =
        p.publicSessionTokenByChatSessionId[chatSessionId] ||
        selectedRow?.publicSessionToken ||
        "";
      if (!sessionToken) {
        p.setPublicError("Unable to open this session right now.");
        return;
      }
      p.setSelectedPublicSessionToken(sessionToken);
      p.notifyPublicSessionTokenChange(sessionToken);
      if (onSessionSelect) onSessionSelect(chatSessionId);
      p.loadPublicSessionDetail(sessionToken, { silent: true });
    },
    [isNewChat, selectedSessionId, isPublicMode, onSessionSelect]
  );

  const handleStartChat = useCallback(
    async (message, imageUrls) => {
      if (isPublicMode) {
        if (!publicShareToken || composerDisabled) return;
        const p = pubRef.current;
        setStartingChat(true);
        setIsNewChat(false);
        setSelectedSessionId("__pending__");
        p.setPublicPendingUserMessage(message);
        p.setPublicError("");
        try {
          const userTimezone =
            Intl.DateTimeFormat().resolvedOptions().timeZone;
          const publicStartPayload = {
            ...(publicIdentityPayload || {}),
            message,
            userTimezone,
            agentId,
          };
          if (imageUrls && imageUrls.length > 0) {
            publicStartPayload.imageUrls = imageUrls;
          }
          const response = await usePublicChatStore
            .getState()
            .startPublicChat(publicShareToken, publicStartPayload);
          const data = response?.data || {};
          const nextPublicSessionToken = data?.publicSessionToken || "";
          if (data?.chatSessionId && nextPublicSessionToken) {
            p.upsertPublicSessionTokenMapping(
              data.chatSessionId,
              nextPublicSessionToken
            );
            p.setSelectedPublicSessionToken(nextPublicSessionToken);
            p.notifyPublicSessionTokenChange(nextPublicSessionToken);
            setSelectedSessionId(data.chatSessionId);
            if (onSessionSelect) onSessionSelect(data.chatSessionId);
            const optimisticUserMsg = {
              messageId: `_optimistic_start_${Date.now()}`,
              role: "user",
              content: message,
              timestamp: new Date().toISOString(),
              imageUrls: imageUrls || undefined,
              senderDisplayName: publicUserDisplayName || undefined,
              _optimistic: true,
            };
            const initialDetail = {
              chatSessionId: data.chatSessionId,
              status: "IN_PROGRESS",
              source: "PUBLIC_CHAT",
              title: agentName || "Agent Chat",
              messages: [optimisticUserMsg],
            };
            useAgentStore.setState({
              sessionDetail: initialDetail,
              chatStatus: "submitted",
              sendError: null,
            });
            p.setPublicError("");
            if (showSidebar) {
              await p.loadPublicSessions({ silent: true });
            }
            await p.loadPublicSessionDetail(nextPublicSessionToken, {
              silent: true,
            });
          } else {
            setIsNewChat(true);
            setSelectedSessionId(null);
            p.setPublicPendingUserMessage(null);
          }
        } catch (error) {
          useAgentStore.setState({ chatStatus: "ready" });
          pubRef.current.setPublicError(
            parsePublicErrorMessage(
              error,
              "Unable to start this chat session."
            )
          );
          pubRef.current.setPublicPendingUserMessage(null);
          setIsNewChat(true);
          setSelectedSessionId(null);
        } finally {
          setStartingChat(false);
        }
        return;
      }

      // Private mode
      setStartingChat(true);
      setIsNewChat(false);
      setSelectedSessionId("__pending__");
      const result = await useAgentStore
        .getState()
        .startChat(agentId, message, imageUrls, undefined, undefined, chatContext);
      setStartingChat(false);
      if (result?.chatSessionId) {
        setSelectedSessionId(result.chatSessionId);
        if (onSessionSelect) onSessionSelect(result.chatSessionId);
      } else {
        setIsNewChat(true);
        setSelectedSessionId(null);
      }
    },
    [
      isPublicMode,
      publicShareToken,
      composerDisabled,
      publicIdentityPayload,
      publicUserDisplayName,
      agentId,
      agentName,
      onSessionSelect,
      showSidebar,
      chatContext,
    ]
  );

  const handleSendPublicMessage = useCallback(
    async (message, imageUrls) => {
      if (!isPublicMode || composerDisabled) return;
      const nextMessage = String(message || "").trim();
      if (!nextMessage) return;
      const p = pubRef.current;
      if (!p.selectedPublicSessionToken) {
        await handleStartChat(nextMessage, imageUrls);
        return;
      }
      const result = await useAgentStore.getState().sendPublicMessage(
        selectedSessionId,
        p.selectedPublicSessionToken,
        agentId,
        nextMessage,
        imageUrls,
        publicUserDisplayName || undefined
      );
      if (result) {
        p.setPublicError("");
        if (showSidebar) {
          p.loadPublicSessions({ silent: true });
        }
      }
    },
    [
      isPublicMode,
      composerDisabled,
      handleStartChat,
      selectedSessionId,
      agentId,
      publicUserDisplayName,
      showSidebar,
    ]
  );

  // --- Effects ---

  // New-chat request nonce
  const previousNewChatRequestNonceRef = useRef(0);
  const handleNewChatRequestNonce = useCallback(
    (nonce) => {
      if (nonce === previousNewChatRequestNonceRef.current) return;
      previousNewChatRequestNonceRef.current = nonce;
      handleNewChat();
    },
    [handleNewChat]
  );

  // Select-session request
  const previousSelectSessionNonceRef = useRef(0);
  const handleSelectSessionRequest = useCallback(
    (request) => {
      if (!request) return;
      if (request.nonce === previousSelectSessionNonceRef.current) return;
      previousSelectSessionNonceRef.current = request.nonce;
      if (request.sessionId) {
        handleSelectSession(request.sessionId);
      }
    },
    [handleSelectSession]
  );

  // Initial session load (private)
  useEffect(() => {
    if (isPublicMode) return;
    if (initialSessionId && !initialLoadDone.current) {
      initialLoadDone.current = true;
      useAgentStore.getState().fetchSessionDetail(initialSessionId);
    }
  }, [initialSessionId, isPublicMode]);

  // Disconnect on unmount
  useEffect(() => {
    return () => useAgentStore.getState().disconnectStream();
  }, []);

  // SSE subscription
  useEffect(() => {
    if (isPublicMode) {
      if (
        !pub.selectedPublicSessionToken ||
        !selectedSessionId ||
        selectedSessionId === "__pending__"
      ) {
        return;
      }
      useAgentStore.getState().subscribeToSession(selectedSessionId, {
        publicSessionToken: pub.selectedPublicSessionToken,
        reconnectWhileSubscribed: true,
      });
      return () => useAgentStore.getState().disconnectStream();
    }
    if (!selectedSessionId || selectedSessionId === "__pending__") return;
    useAgentStore.getState().subscribeToSession(selectedSessionId, {
      reconnectWhileSubscribed: true,
    });
    return () => useAgentStore.getState().disconnectStream();
  }, [isPublicMode, selectedSessionId, pub.selectedPublicSessionToken]);

  // Agent-level SSE subscription
  useEffect(() => {
    if (isPublicMode) return;
    if (!agentId) return;
    useAgentStore.getState().subscribeToAgent(agentId);
    return () => useAgentStore.getState().disconnectAgentStream();
  }, [agentId, isPublicMode]);

  // Private session polling
  useEffect(() => {
    if (isPublicMode) return;
    if (!agentId) return;
    const id = setInterval(() => {
      useAgentStore.getState().fetchSessions(agentId, { silent: true });
    }, 10000);
    return () => clearInterval(id);
  }, [agentId, isPublicMode]);

  // Status-change effect — refresh session list when status changes
  const handleStatusChange = useCallback(
    (currentStatus) => {
      if (isPublicMode) return;
      if (
        prevStatusRef.current !== currentStatus &&
        selectedSessionId &&
        selectedSessionId !== "__pending__"
      ) {
        useAgentStore.getState().refreshSessionInList(selectedSessionId);
        if (
          prevStatusRef.current === "IN_PROGRESS" &&
          currentStatus &&
          currentStatus !== "IN_PROGRESS"
        ) {
          useAgentStore.getState().fetchSessions(agentId, { silent: true });
        }
      }
      prevStatusRef.current = currentStatus;
    },
    [isPublicMode, selectedSessionId, agentId]
  );

  return {
    selectedSessionId,
    setSelectedSessionId,
    isNewChat,
    startingChat,
    resolvePrivateClientId,
    handleNewChat,
    handleSelectSession,
    handleStartChat,
    handleSendPublicMessage,
    handleNewChatRequestNonce,
    handleSelectSessionRequest,
    handleStatusChange,
  };
}
